import { env } from "./env";
import { useAuthStore } from "@/stores/authStore";

export type AnalyticsEventType =
  | "xhs.land"
  | "diagnosis.start"
  | "diagnosis.complete"
  | "diagnosis.share_card_generated"
  | "diagnosis.share_click"
  | "diagnosis.midway_feedback_shown"
  | "diagnosis.abandon"
  | "diagnosis.report_view"
  | "diagnosis.report_save_click"
  | "diagnosis.report_training_click"
  | "diagnosis.report_wechat_recall_view"
  | "diagnosis.report_wechat_recall_click"
  | "free_training.start"
  | "free_training.complete"
  | "free_summary.upsell_click"
  | "free_summary.retain_accept"
  | "app_home.unlock_click"
  | "app_home.upgrade_notice_view"
  | "app_home.daily_mission_start"
  | "pricing.view"
  | "pricing.cta_click"
  | "pricing.channel_select"
  | "pricing.back_to_app"
  | "pricing.continue_free_click"
  | "pricing.all_unlocked"
  | "pricing.upgrade_notice_view"
  | "practice_result.unlock_click"
  | "practice_result.next_chapter"
  | "practice_result.again"
  | "practice.session.resume"
  | "practice.session.start"
  | "xiaoetong.redirect"
  | "xiaoetong.return"
  | "xianyu.redirect"
  | "activate.view"
  | "activate.success"
  | "activate.fail"
  | "wechat_follow.shown"
  | "wechat_follow.qr_clicked"
  | "login.success"
  | "register.success"
  | "review.page_view"
  | "review.srs_banner_click"
  | "review.tag_card_expand"
  | "review.tag_drill_start"
  | "review.weakness_clear"
  | "review.archive_expand"
  | "review.tab_select"
  | "learning_map.chapter_open"
  | "learning_map.chapter_menu_open"
  | "learning_map.locked_click"
  | "learning_map.review_click"
  | "learning_map.grammar_open"
  | "learning_map.pack_switch"
  | "learning_map.pack_switch_unlock_click"
  | "daily_mission.complete"
  | "frontend_error";

export interface AnalyticsPayload {
  [key: string]: unknown;
}

interface AnalyticsBody {
  type: AnalyticsEventType;
  payload?: AnalyticsPayload;
  anonymous_id?: string;
}

export function track(type: AnalyticsEventType, payload?: AnalyticsPayload): void {
  if (typeof navigator === "undefined") return;

  const { token, anonymousId } = useAuthStore.getState();

  // No actor → backend returns 401. Skip the request rather than spam the console.
  if (!token && !anonymousId) return;

  const body: AnalyticsBody = { type };
  if (payload) body.payload = payload;
  if (anonymousId) body.anonymous_id = anonymousId;

  const url = `${env.apiBaseUrl}/api/events`;
  const json = JSON.stringify(body);

  // sendBeacon can't set Authorization, so authenticated users must use fetch.
  // Anonymous users can use sendBeacon since body.anonymous_id is the backend fallback.
  if (!token && typeof navigator.sendBeacon === "function") {
    // sendBeacon requires a Blob with the right Content-Type, otherwise FastAPI returns 422.
    const blob = new Blob([json], { type: "application/json" });
    if (navigator.sendBeacon(url, blob)) return;
  }

  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  else if (anonymousId) headers["X-Anonymous-Id"] = anonymousId;

  fetch(url, {
    method: "POST",
    headers,
    body: json,
    keepalive: true,
  }).catch(() => {
    // analytics is best-effort; swallow errors
  });
}
